import { Module } from 'vuex';
import { AuthClient } from "../api/ApiClientBase";
import * as api from "../api/ApiClient";
import { ActivityValidator } from "../api/ActivityValidator";

import * as jsPlumbToolkit from 'jsplumbtoolkit';
import { WorkflowNotificationsHandler } from '../api/WorkflowNotificationsHandler';
import BaseEditableNode from '../components/pages/workflow/Nodes/BaseEditableNode.vue';
import { isFeatureFlagEnabled } from '@/services/featureFlagService';
import { isForbiddenError } from '../utils/ApiUtils';

async function schedulesOrEmptyIfForbidden(
    load: Promise<api.ProcessSchedule[]>,
): Promise<api.ProcessSchedule[]> {
    try {
        return await load;
    } catch (error) {
        if (!isForbiddenError(error)) {
            throw error;
        }
        return [];
    }
}

export interface EnrichedProcessNode extends api.InteractiveProcessNode {
    _owners: string | null | undefined;
    _supervisors: string | null | undefined;
    _contributors: string | null | undefined;
    _activityName: string | null | undefined;
    _calculatedDueDate: Date | null | undefined;
    _parentCaption: string | null | undefined;
}

export interface IWorkflowStore {
    IsBusy: boolean;

    SelectedActivity: api.BaseProcessActivity | null;
    SelectedActivityInitialValue: string | null;
    SelectedNode: api.InteractiveProcessNode | null;
    SelectedEdge: jsPlumbToolkit.Edge | null;
    SelectedActivityValidator: ActivityValidator | null;
    Workflow: api.InteractiveProcess | null;
    WorkflowState: api.ProcessActivityExecutionState[] | null;
    WorkflowStateByNodeId: Map<string, api.ProcessActivityExecutionState[]>;
    FrozenWorkflowState: api.ProcessActivityExecutionState[] | null;
    FrozenWorkflowStateByNodeId: Map<string, api.ProcessActivityExecutionState[]>;
    SelectedSubprocessRun: string;
    Schedules: api.ProcessSchedule[] | null;
    ParentWorkflow: api.InteractiveProcess[];
    CurrentSubprocessNodes: api.InteractiveProcessNode[];
    ContextMenuInfo: { node: BaseEditableNode; event: MouseEvent } | null;

    AvailableDataSources: api.DataSource[] | null;
    AvailableDataPipelines: api.PipelineDefinitionItem[] | null;
    IsEditable: boolean;
    IsNew: boolean;
    ProcessRunId: string | null;
    WorkflowPath: string | null;
    WorkflowName: string | null;

    NotificationHandler: WorkflowNotificationsHandler | null;
    LastReceivedNotification: api.ProcessRunActivity | null;

    ZoomLevel: number;

    IsGlobalWorkflow: boolean;

    ReadOnlyWfWhileRunningFlagTask: Promise<boolean> | null;
    freezeWorkflowStateOnFinishFlag: boolean | null;
    WorkflowStatePerfIndexFlagTask: Promise<boolean> | null;
    WorkflowStatePerfIndexEnabled: boolean;
    LevelSaveGuardFlagTask: Promise<boolean> | null;
    LevelSaveGuardEnabled: boolean;
}

function levelExitWouldNestAncestor(state: IWorkflowStore, exitingNode: api.InteractiveProcessNode, exported: api.InteractiveProcess): boolean {
    const ancestorIds = new Set<string>();
    [...state.CurrentSubprocessNodes, exitingNode].forEach(n => n.id && ancestorIds.add(n.id));
    state.ParentWorkflow.forEach(level => level?.nodes?.forEach(n => n.id && ancestorIds.add(n.id)));
    return exported?.nodes?.some(n => n.id != null && ancestorIds.has(n.id)) ?? false;
}

function buildWorkflowStateIndex(workflowState: api.ProcessActivityExecutionState[] | null): Map<string, api.ProcessActivityExecutionState[]> {
    const index = new Map<string, api.ProcessActivityExecutionState[]>();
    if (workflowState == null) {
        return index;
    }

    for (const entry of workflowState) {
        if (!entry.nodeId) {
            continue;
        }

        const entriesForNode = index.get(entry.nodeId);
        if (entriesForNode) {
            entriesForNode.push(entry);
        } else {
            index.set(entry.nodeId, [entry]);
        }
    }

    return index;
}

const WorkflowStore: Module<IWorkflowStore, any> = {
    namespaced: true,
    state: {
        IsBusy: false,

        SelectedActivity: null,
        SelectedActivityInitialValue: null,
        SelectedNode: null,
        SelectedEdge: null,
        SelectedActivityValidator: null,
        Workflow: null,
        WorkflowState: null,
        WorkflowStateByNodeId: new Map(),
        FrozenWorkflowState: null,
        FrozenWorkflowStateByNodeId: new Map(),
        SelectedSubprocessRun: "",
        Schedules: null,
        ParentWorkflow: [],
        CurrentSubprocessNodes: [],
        ContextMenuInfo: null,

        AvailableDataSources: null,
        AvailableDataPipelines: null,

        IsEditable: false,
        IsNew: false,
        ProcessRunId: null,
        WorkflowPath: null,
        WorkflowName: null,

        NotificationHandler: null,
        LastReceivedNotification: null,

        ZoomLevel: 1,

        IsGlobalWorkflow: false,

        ReadOnlyWfWhileRunningFlagTask: null,

        freezeWorkflowStateOnFinishFlag: null,

        WorkflowStatePerfIndexFlagTask: null,
        WorkflowStatePerfIndexEnabled: false,

        LevelSaveGuardFlagTask: null,
        LevelSaveGuardEnabled: false,
    },

    mutations: {
        ClearWorkflowInstanceVariables(state) {
            state.SelectedActivity = state.SelectedActivityInitialValue = state.SelectedNode = state.SelectedEdge = state.SelectedActivityValidator
                = state.ProcessRunId = null;
            state.FrozenWorkflowState = null;
            state.FrozenWorkflowStateByNodeId = new Map();
            state.ParentWorkflow = [];
            state.CurrentSubprocessNodes = [];
        },

        SelectActivity(state, val: api.BaseProcessActivity | null) {
            state.SelectedActivity = val;
            if (val) {
                state.SelectedActivityInitialValue = JSON.stringify(val);
                state.SelectedActivityValidator = ActivityValidator.For(val);
            }
            else {
                state.SelectedActivityInitialValue = "";
                state.SelectedActivityValidator = null;
            }
        },

        SetSelectedNode(state, node: api.InteractiveProcessNode | null) {
            state.SelectedNode = node;
            if (node != null) {
                let activity = node.activity!;
                state.SelectedActivity = activity;
                if (activity.hasOwnProperty('dueDate')) {
                    if ((<api.InteractiveProcessActivity>activity).dueDate == null) {
                        (<api.InteractiveProcessActivity>activity).dueDate == '';
                    }
                }
                state.SelectedActivityInitialValue = JSON.stringify(activity);
                state.SelectedActivityValidator = ActivityValidator.For(activity);
            }
            else {
                state.SelectedActivity = null;
                state.SelectedActivityInitialValue = "";
                state.SelectedActivityValidator = null;
            }
        },

        SetSubprocess(state, node: api.InteractiveProcessNode | null) {
            state.SelectedNode = node;
            if (node != null) {
                if (node.activity instanceof (api.SubProcessProcessActivity)) {
                    state.ParentWorkflow.push(state.Workflow!);
                    state.CurrentSubprocessNodes.push(node);
                    state.Workflow = (node.activity as api.SubProcessProcessActivity).subProcess!;
                    state.SelectedSubprocessRun = "Definition";
                }
            }
            else {
                state.SelectedActivity = null;
                state.SelectedActivityInitialValue = "";
                state.SelectedActivityValidator = null;
                state.SelectedSubprocessRun = "";

            }
        },

        SetWorkflowAndDrillDown(state, payload: { workflow: api.InteractiveProcess; node: api.InteractiveProcessNode }) {
            state.Workflow = payload.workflow;
            state.SelectedNode = payload.node;
            if (payload.node.activity instanceof api.SubProcessProcessActivity) {
                state.ParentWorkflow.push(state.Workflow);
                state.CurrentSubprocessNodes.push(payload.node);
                state.Workflow = payload.node.activity.subProcess!;
                state.SelectedSubprocessRun = "Definition";
            }
        },

        SetWorkflowWithSubprocessPath(state, payload: { workflow: api.InteractiveProcess; subprocessNodes: api.InteractiveProcessNode[] }) {
            let currentWorkflow = payload.workflow;
            state.ParentWorkflow = [];
            state.CurrentSubprocessNodes = [];

            for (const node of payload.subprocessNodes) {
                state.ParentWorkflow.push(currentWorkflow);
                state.CurrentSubprocessNodes.push(node);
                currentWorkflow = (node.activity as api.SubProcessProcessActivity).subProcess!;
            }

            state.Workflow = currentWorkflow;
            state.SelectedSubprocessRun = "Definition";
            state.SelectedNode = payload.subprocessNodes[payload.subprocessNodes.length - 1];
        },

        SetSelectedEdge(state, edge: jsPlumbToolkit.Edge | null) {
            state.SelectedEdge = edge;
        },

        EndEditSubprocess(state, workflow: api.InteractiveProcess) {
            var node = state.CurrentSubprocessNodes.pop();
            if (state.LevelSaveGuardEnabled && levelExitWouldNestAncestor(state, node!, workflow))
                console.warn(`Rejected level exit for ${node!.id}: the exported level contains its own or an ancestor's activity ids.`);
            else
                (node!.activity as api.SubProcessProcessActivity).subProcess = workflow;
            state.Workflow = state.ParentWorkflow.pop()!;

            state.SelectedActivity = null;
            state.SelectedNode = null;
            state.SelectedActivityInitialValue = "";
            state.SelectedActivityValidator = null;
            if (state.CurrentSubprocessNodes.length > 0)
                state.SelectedSubprocessRun = "Definition";
            else
                state.SelectedSubprocessRun = "";
        },

        SetWorkflow(state, Workflow: api.InteractiveProcess | null) {
            state.Workflow = Workflow;
        },

        SetWorkflowState(state, WorkflowState: api.ProcessActivityExecutionState[] | null) {
            state.WorkflowState = WorkflowState;
            state.WorkflowStateByNodeId = state.WorkflowStatePerfIndexEnabled
                ? buildWorkflowStateIndex(WorkflowState)
                : new Map();
        },

        FreezeWorkflowState(state) {
            state.FrozenWorkflowState = state.WorkflowState ? [...state.WorkflowState] : null;
            state.FrozenWorkflowStateByNodeId = state.WorkflowStatePerfIndexEnabled
                ? buildWorkflowStateIndex(state.FrozenWorkflowState)
                : new Map();
        },

        ClearFrozenWorkflowState(state) {
            state.FrozenWorkflowState = null;
            state.FrozenWorkflowStateByNodeId = new Map();
        },

        SetWorkflowSchedules(state, schedules: api.ProcessSchedule[] | null) {
            state.Schedules = schedules;
        },

        NewWorkflow(state, path: string) {
            state.SelectedActivity = state.SelectedActivityInitialValue = state.SelectedNode = state.SelectedEdge = state.SelectedActivityValidator
                = state.ProcessRunId = null;
            state.ParentWorkflow = [];
            state.CurrentSubprocessNodes = []

            state.IsNew = true;
            state.WorkflowPath = path;
            var parts = path.split("/");
            state.WorkflowName = parts[parts.length - 1];

            var wf = new api.InteractiveProcess();
            wf.edges = [];
            wf.nodes = [];
            wf.startProperties = new api.InteractiveProcessStartProperties()
            wf.startProperties.dueDate = "";

            wf.nodes.push(new api.InteractiveProcessNode());
            wf.nodes[0].left = 0;
            wf.nodes[0].top = 50;
            wf.nodes[0].type = "start";
            wf.nodes[0].w = 200;
            wf.nodes[0].h = 100;
            wf.nodes[0].text = "START";
            wf.nodes[0].state = "Upcoming";
            wf.nodes[0].id = jsPlumbToolkit.jsPlumbUtil.uuid();
            wf.nodes[0].text = "START";

            var spa = new api.StartProcessActivity();
            spa.disabled = false;
            spa.name = "Start";
            spa.timeout = 0;

            wf.nodes![0].activity = spa;

            state.Workflow = wf
        },

        SetOldWorkflowPath(state, path: string) {
            state.WorkflowPath = path;
            state.IsNew = false;
        },

        SetWorkflowName(state, name: string) {
            state.WorkflowName = name;
        },


        SetNotificationHandler(state, notificationHandler: WorkflowNotificationsHandler) {
            if (state.NotificationHandler != null)
                state.NotificationHandler.stop();
            state.NotificationHandler = notificationHandler;
            state.NotificationHandler.start();
        },

        SetLastReceivedNotification(state, activity: api.ProcessRunActivity) {
            state.LastReceivedNotification = activity;
        },

        SetSelectedSubprocessRun(state, subprocessRun: string) {
            state.SelectedSubprocessRun = subprocessRun;
        },


        SetAvailableDataSources(state, dataSources: api.DataSource[]) {
            state.AvailableDataSources = dataSources;
        },
        SetAvailableDataPipelines(state, pipelines: api.PipelineDefinitionItem[]) {
            state.AvailableDataPipelines = pipelines;
        },

        SetZoomLevel(state, zoom: number) {
            if (zoom && zoom > 0)
                state.ZoomLevel = zoom;
        },

        SetIsBusy(state, busy: boolean) {
            state.IsBusy = busy;
        },

        SetProcessRunId(state, processRunId: string) {
            state.ProcessRunId = processRunId;
        },

        SetIsNew(state, isnew: boolean) {
            state.IsNew = isnew;
        },

        SetIsEditable(state, isEditable: boolean) {
            state.IsEditable = isEditable;
        },



        FixSubprocessActivity(state, nodeId: string) {
            var subprocessNode = state.Workflow!.nodes!.find(node => node.id == nodeId)!;
            var subprocessActivity = subprocessNode.activity;
            var subprocess = (subprocessActivity as any as api.SubProcessProcessActivity).subProcess;
            var newSubProcess = (subprocessActivity as any as api.SubProcessProcessActivity).subProcess = new api.InteractiveProcess();
            newSubProcess.nodes = [];
            newSubProcess.edges = [];
            for (var j = 0; j < subprocess!.nodes!.length; j++) {
                var node = subprocess!.nodes![j];
                var newNode = new api.InteractiveProcessNode();
                newNode.activity = node.activity;
                newNode.h = node.h;
                newNode.id = node.id;
                newNode.left = node.left;
                newNode.state = node.state;
                newNode.text = node.text;
                newNode.top = node.top;
                newNode.type = node.type;
                newNode.w = node.w;
                newSubProcess.nodes!.push(newNode);
            }
            for (var j = 0; j < subprocess!.edges!.length; j++) {
                var edge = subprocess!.edges![j];
                var newEdge = new api.InteractiveProcessEdge();
                newEdge.data = new api.InteractiveProcessEdgeData();
                newEdge.data.id = edge.data!.id
                newEdge.data.label = edge.data!.label
                newEdge.data.type = edge.data!.type
                newEdge.source = edge.source;
                newEdge.target = edge.target;
                newEdge.geometry = edge.geometry;
                newSubProcess.edges!.push(newEdge);
            }
        },

        FixSubprocessActivityNode(state, subprocessNode: api.InteractiveProcessNode) {
            var subprocessActivity = subprocessNode.activity;
            var subprocess = (subprocessActivity as any as api.SubProcessProcessActivity).subProcess;
            var newSubProcess = (subprocessActivity as any as api.SubProcessProcessActivity).subProcess = new api.InteractiveProcess();
            newSubProcess.nodes = [];
            newSubProcess.edges = [];
            for (var j = 0; j < subprocess!.nodes!.length; j++) {
                var node = subprocess!.nodes![j];
                var newNode = new api.InteractiveProcessNode();
                newNode.activity = node.activity;
                newNode.h = node.h;
                newNode.id = node.id;
                newNode.left = node.left;
                newNode.state = node.state;
                newNode.text = node.text;
                newNode.top = node.top;
                newNode.type = node.type;
                newNode.w = node.w;
                newSubProcess.nodes!.push(newNode);
            }
            for (var j = 0; j < subprocess!.edges!.length; j++) {
                var edge = subprocess!.edges![j];
                var newEdge = new api.InteractiveProcessEdge();
                newEdge.data = new api.InteractiveProcessEdgeData();
                newEdge.data.id = edge.data!.id
                newEdge.data.label = edge.data!.label
                newEdge.data.type = edge.data!.type
                newEdge.source = edge.source;
                newEdge.target = edge.target;
                newEdge.geometry = edge.geometry;
                newSubProcess.edges!.push(newEdge);
            }
        },

        SetContextMenuInfo(state, info: { node: BaseEditableNode; event: MouseEvent } | null) {
            state.ContextMenuInfo = info;
        },

        SetIsGlobalWorkflow(state, value: boolean) {
            state.IsGlobalWorkflow = value;
        },

        SetFreezeWorkflowStateOnFinishFlag(state, value: boolean) {
            state.freezeWorkflowStateOnFinishFlag = value;
        },

        SetReadOnlyWfWhileRunningFlagTask(state, value: Promise<boolean>) {
            state.ReadOnlyWfWhileRunningFlagTask = value;
        },

        SetWorkflowStatePerfIndexFlagTask(state, value: Promise<boolean>) {
            state.WorkflowStatePerfIndexFlagTask = value;
        },

        SetWorkflowStatePerfIndexEnabled(state, value: boolean) {
            state.WorkflowStatePerfIndexEnabled = value;
            state.WorkflowStateByNodeId = value ? buildWorkflowStateIndex(state.WorkflowState) : new Map();
            state.FrozenWorkflowStateByNodeId = value ? buildWorkflowStateIndex(state.FrozenWorkflowState) : new Map();
        },

        SetLevelSaveGuardFlagTask(state, value: Promise<boolean>) {
            state.LevelSaveGuardFlagTask = value;
        },

        SetLevelSaveGuardEnabled(state, value: boolean) {
            state.LevelSaveGuardEnabled = value;
        }
    },

    actions: {
        async isFreezeWorkflowStateOnFinishEnabled(context) {
            if (context.state.freezeWorkflowStateOnFinishFlag !== null) return context.state.freezeWorkflowStateOnFinishFlag;
            const f = await isFeatureFlagEnabled("PHOS-3174_freezeWorkflowStateOnFinish");
            context.commit("SetFreezeWorkflowStateOnFinishFlag", f);
            return f;
        },

        async isReadOnlyWfWhileRunningEnabled(context) {
            if (context.state.ReadOnlyWfWhileRunningFlagTask === null) {
                const flagPromise = isFeatureFlagEnabled("PHOS-3480_readOnlyWfWhileRunning");
                context.commit("SetReadOnlyWfWhileRunningFlagTask", flagPromise);
            }
            return await context.state.ReadOnlyWfWhileRunningFlagTask;
        },

        async isWorkflowStatePerfIndexEnabled(context) {
            if (context.state.WorkflowStatePerfIndexFlagTask === null) {
                const flagPromise = isFeatureFlagEnabled("PHOS-3573_workflowStatePerfIndex").catch(() => false);
                context.commit("SetWorkflowStatePerfIndexFlagTask", flagPromise);
            }

            const enabled = await context.state.WorkflowStatePerfIndexFlagTask;
            context.commit("SetWorkflowStatePerfIndexEnabled", enabled);
            return enabled;
        },

        async isLevelSaveGuardEnabled(context) {
            if (context.state.LevelSaveGuardFlagTask === null) {
                const flagPromise = isFeatureFlagEnabled("PHOS-2696_workflowLevelSaveGuard").catch(() => false);
                context.commit("SetLevelSaveGuardFlagTask", flagPromise);
            }

            const enabled = await context.state.LevelSaveGuardFlagTask;
            context.commit("SetLevelSaveGuardEnabled", enabled);
            return enabled;
        },

        async WorkflowRunFinished(context) {
            this.dispatch("ShowToast", new api.ToastNotification({
                message: context.rootState.Localizer.Localize("WorkflowStore_Workflow_run_finished"),
                type: api.ToastType.Info
            }));

            if (await context.dispatch("isFreezeWorkflowStateOnFinishEnabled")) {
                context.commit("FreezeWorkflowState");
            }
                
            if (context.state.NotificationHandler != null) {
                // clear old ID before next await to avoid race condition with notifications
                context.state.NotificationHandler.processRunId = null;
            }
            context.commit("SetProcessRunId", null);
        },

        async LoadWorkflow(context, fso: api.FileSystemObjectContent) {
            context.commit("SetZoomLevel", 1);
            context.commit("SetIsEditable", true);
            while (context.state.IsBusy == true) {
                //avoid loading 2 processes at the same time.
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            context.commit("SetIsBusy", true);
            try {
                await context.dispatch("isWorkflowStatePerfIndexEnabled");
                await context.dispatch("isLevelSaveGuardEnabled");
                var name = fso.path + "/" + fso.name;
                while (name.startsWith('//')) // fix for parent being the root
                    name = name.replace("//", "/");
                context.commit("ClearWorkflowInstanceVariables");
                context.commit("SetOldWorkflowPath", name);
                context.commit("SetWorkflowName", fso.name);

                var auth_client = AuthClient.getInstance();
                await auth_client.ensureToken();
                var client = new api.ProcessClient(auth_client);
                var client2 = new api.ProcessExecutionClient(auth_client);

                var processPromise = client.loadInteractive(name);
                var workflowStatePromise = client.getInteractiveProcessExecutionState(name);
                var schedulesPromise = client2.getSchedule(name);

                var process = await processPromise;
                var workflowState = await workflowStatePromise;
                var schedules = await schedulesOrEmptyIfForbidden(schedulesPromise);

                context.commit("SetProcessRunId", null);
                for (var j = 0; j < workflowState.length; j++) {
                    if (workflowState[j].processRunId != null) {
                        context.commit("SetProcessRunId", workflowState[j].processRunId);
                        break;
                    }
                }

                console.log(workflowState);

                context.commit("SetWorkflowState", workflowState);
                context.commit("SetWorkflow", process);
                context.commit("SetWorkflowSchedules", schedules);

            }
            finally {
                context.commit("SetIsBusy", false);
            }
        },

        async CreateSchedule(context, schedule: api.ProcessSchedule) {

            context.state.Schedules!.push(schedule);
        },

        async SaveWorkflow(context) {
            try {
                context.commit("SetIsBusy", true);
                var auth_client = AuthClient.getInstance();
                await auth_client.ensureToken();

                var client = new api.ProcessClient(auth_client);
                await context.dispatch("FixSubprocessActivities");
                await client.saveInteractive(context.state.WorkflowPath!, context.state.Workflow!);

                //var ModuleSecurityAccess = context.state.security.ModuleSecurity;
                //var UserCanScheduleWorkflow = ModuleSecurityAccess != null && ModuleSecurityAccess.includes(api.ModuleAccess.WorkflowManagementManage);

                if (true) {
                    var client2 = new api.ProcessExecutionClient(auth_client);
                    if (context.state.Schedules != null)
                        await client2.setSchedule(context.state.Schedules!, context.state.WorkflowPath!);
                }

                context.commit("SetOldWorkflowPath", context.state.WorkflowPath);
                this.dispatch("ShowToast", new api.ToastNotification({
                    message: context.rootState.Localizer.Localize("WorkflowStore_Workflow_saved"),
                    type: api.ToastType.Info
                }));
                console.log("Workflow " + context.state.WorkflowPath! + " saved!");
            }
            finally {
                context.commit("SetIsBusy", false);
            }
        },

        async FixSubprocessActivities(context) {
            var fix = function (workflow: api.InteractiveProcess | null | undefined) {
                if (workflow == null || workflow.nodes == null)
                    return;

                for (var i = 0; i < workflow.edges!.length; i++) {
                    if (workflow.edges![i].geometry != null && !(workflow.edges![i].geometry instanceof (api.InteractiveProcessEdgeGeometryData))) {
                        var geometry = workflow.edges![i].geometry;
                        workflow.edges![i].geometry = new api.InteractiveProcessEdgeGeometryData();
                        workflow.edges![i].geometry!.segments = geometry!.segments;
                        workflow.edges![i].geometry!.source = geometry!.source;
                        workflow.edges![i].geometry!.target = geometry!.target;
                    }
                }

                for (var i = 0; i < workflow.nodes.length; i++) {
                    if (workflow.nodes[i]!.activity instanceof (api.SubProcessProcessActivity)) {
                        context.commit("FixSubprocessActivityNode", workflow.nodes[i]);
                        fix((workflow.nodes[i]!.activity as api.SubProcessProcessActivity).subProcess);
                    }
                }
            }

            fix(context.state.Workflow!)
        },

        async RunWorkflow(context, options: string[][] | null) {
            if (context.state.IsNew)
                return;
            context.commit("SetIsBusy", true);
            try {
                var auth_client = AuthClient.getInstance();
                await auth_client.ensureToken();
                var client = new api.ProcessExecutionClient(auth_client);
                if (options == undefined || options == null)
                    options = [];
                if (context.state.NotificationHandler != null) {
                    // clear old ID before next await to avoid race condition with notifications
                    context.state.NotificationHandler.processRunId = null;
                }
                context.state.NotificationHandler!.isPendingRun = true;
                context.commit("ClearFrozenWorkflowState");
                var id = await client.executeInteractiveFileSystem(context.state.WorkflowPath!, options);
                context.state.NotificationHandler!.processRunId = id;
                context.commit("SetProcessRunId", id);
                this.dispatch("ShowToast", new api.ToastNotification({
                    message: context.rootState.Localizer.Localize("WorkflowStore_Workflow_started"),
                    type: api.ToastType.Info
                }));
                console.log(context.rootState.Localizer.Localize("WorkflowStore_notifications_started"));
            }
            finally {
                context.state.NotificationHandler!.isPendingRun = false;
                context.commit("SetIsBusy", false);
            }
        },

        async RefreshState(context) {
            await context.dispatch("isWorkflowStatePerfIndexEnabled");
            const auth_client = AuthClient.getInstance();
            await auth_client.ensureToken();
            const client = new api.ProcessClient(auth_client);
            const workflowPath = context.state.WorkflowPath;
            if (workflowPath != null) {
                var workflowState = await client.getInteractiveProcessExecutionState(workflowPath);
                context.commit("SetWorkflowState", workflowState);
            }
        },

        async RefreshWorkflow(context) {
            // This function was initially built by Github Copilot
            if (!context.state.WorkflowPath) {
                console.error("Workflow path is not set. Cannot refresh workflow.");
                return;
            }

            context.commit("SetIsBusy", true);
            try {
                const useWorkflowStatePerfIndex = await context.dispatch("isWorkflowStatePerfIndexEnabled");
                const levelSaveGuard = await context.dispatch("isLevelSaveGuardEnabled");
                // Clear current workflow state
                console.log("State: ", this.state);
                var currentSubprocessNodes = [...context.state.CurrentSubprocessNodes];
                if (!levelSaveGuard)
                    context.commit("ClearWorkflowInstanceVariables");

                // Fetch the workflow data again
                const auth_client = AuthClient.getInstance();
                await auth_client.ensureToken();
                const client = new api.ProcessClient(auth_client);
                const client2 = new api.ProcessExecutionClient(auth_client);

                const workflowPath = context.state.WorkflowPath;
                const processPromise = client.loadInteractive(workflowPath);
                const workflowStatePromise = client.getInteractiveProcessExecutionState(workflowPath);
                const schedulesPromise = schedulesOrEmptyIfForbidden(
                    client2.getSchedule(workflowPath),
                );

                const [process, workflowState, schedules] = await Promise.all([
                    processPromise,
                    workflowStatePromise,
                    schedulesPromise,
                ]);

                if (levelSaveGuard)
                    context.commit("ClearWorkflowInstanceVariables");
                context.commit("SetWorkflowState", workflowState);
                context.commit("SetWorkflowSchedules", schedules);

                // Update the process run ID if applicable
                const activeRun = workflowState.find((state) => state.processRunId != null);
                context.commit("SetProcessRunId", activeRun ? activeRun.processRunId : null);

                if (useWorkflowStatePerfIndex && currentSubprocessNodes.length > 0) {
                    let currentWorkflow = process;
                    const rebuiltPath: api.InteractiveProcessNode[] = [];

                    for (const previousNode of currentSubprocessNodes) {
                        const currentNode = currentWorkflow.nodes?.find(node => node.id === previousNode.id);
                        if (!(currentNode?.activity instanceof api.SubProcessProcessActivity) || !currentNode.activity.subProcess) {
                            break;
                        }

                        rebuiltPath.push(currentNode);
                        currentWorkflow = currentNode.activity.subProcess;
                    }

                    if (rebuiltPath.length === currentSubprocessNodes.length) {
                        context.commit("SetWorkflowWithSubprocessPath", { workflow: process, subprocessNodes: rebuiltPath });
                    } else {
                        context.commit("SetWorkflow", process);
                    }
                } else {
                    context.commit("SetWorkflow", process);
                    for (const subprocessNode of currentSubprocessNodes) {
                        context.state.SelectedNode = subprocessNode;
                        context.commit("SetSubprocess", subprocessNode);
                    }
                }

                console.log("Workflow refreshed successfully.");
            } catch (error) {
                console.error("Failed to refresh workflow:", error);
                this.dispatch("ShowToast", new api.ToastNotification({
                    message: context.rootState.Localizer.Localize("WorkflowStore_Refresh_failed"),
                    type: api.ToastType.Error,
                }));
            } finally {
                context.commit("SetIsBusy", false);
            }
        },
        async StopWorkflow(context) {
            if (context.state.ProcessRunId != null) {
                context.commit("SetIsBusy", true);
                try {
                    var auth_client = AuthClient.getInstance();
                    await auth_client.ensureToken();
                    var client = new api.ProcessExecutionClient(auth_client);
                    await client.stopInteractiveProcess(context.state.ProcessRunId);
                    if (context.state.NotificationHandler != null)
                        context.state.NotificationHandler.stop();
                    context.commit("SetProcessRunId", null);
                    context.commit("SetWorkflowState", null);

                    this.dispatch("ShowToast", new api.ToastNotification({
                        message: context.rootState.Localizer.Localize("WorkflowStore_Workflow_stopped"),
                        type: api.ToastType.Info
                    }));
                }
                finally {
                    context.commit("SetIsBusy", false);
                }
            }
        },

        async LoadDataSources(context) {
            context.commit("SetIsBusy", true);
            try {
                if (context.state.AvailableDataSources == null) {
                    var auth_client = AuthClient.getInstance();
                    await auth_client.ensureToken();
                    var client = new api.DataSourceClient(auth_client);
                    var datasources = await client.getAvailableDataSources();
                    context.commit("SetAvailableDataSources", datasources);
                }
            }
            finally {
                context.commit("SetIsBusy", false);
            }
        },

        async GetStartProperties(context): Promise<api.FormParameter[] | null | undefined> {
            context.commit("SetIsBusy", true);

            try {
                var workflow = context.state.Workflow;
                if (context.state.ParentWorkflow != null && context.state.ParentWorkflow.length > 0)
                    workflow = context.state.ParentWorkflow[0];
                if (workflow == null || workflow.startProperties == null || workflow.startProperties!.processPropertiesFormPath == null)
                    return [];

                var formPath = workflow.startProperties!.processPropertiesFormPath;

                if (formPath != null) {
                    var auth_client = AuthClient.getInstance();
                    await auth_client.ensureToken();
                    var client = new api.FormClient(auth_client);
                    var formparameters = await client.getFormParameters(formPath);
                    if (formparameters.parameters) {
                        formparameters.parameters.forEach(p => p.currentValue = new api.FormParameterValue({
                            isLeaf: true,
                            displayOnly: true
                        }));
                    }
                    return formparameters.parameters;
                }
            }
            finally {
                context.commit("SetIsBusy", false);
            }
        },
        async SetWorkflowNotificationHandler(context) {
            const handler = new WorkflowNotificationsHandler(context);
            context.commit("SetNotificationHandler", handler);
        }
    },

    getters: {
        selectedActivityValidator(state):ActivityValidator | null {
            return state.SelectedActivityValidator;
        },
        getAdvanceRules(state, getters, rootState): any[] {
            var rez = [];
            rez.push({ value: "", text: rootState.Localizer.Localize("WorkflowStore_Every_time_a_predecessor_completes") });
            rez.push({ value: api.AdvanceRule.WhenAllPredecesors, text: rootState.Localizer.Localize("WorkflowStore_All_predecessors_have_completed") });
            rez.push({ value: api.AdvanceRule.WhenFirstPredecesor, text: rootState.Localizer.Localize("WorkflowStore_First_time_a_predecessor_completes") });
            return rez.sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
        },

        getAllActivities(state): api.InteractiveProcessNode[] {
            var result: api.InteractiveProcessNode[] = [];
            if (state.Workflow) {
                var getAllActivitiesInner = function (process: api.InteractiveProcess) {
                    if (process.nodes)
                        for (var node of process.nodes!) {
                            if (node.activity instanceof (api.SubProcessProcessActivity)) {
                                if (node.activity.subProcess)
                                    getAllActivitiesInner(node.activity.subProcess);
                            }
                            else if (node.activity instanceof (api.NoteProcessActivity) || node.activity instanceof (api.StartProcessActivity))
                                continue;
                            else
                                result.push(node);
                        }
                }
                getAllActivitiesInner(state.Workflow);
            }
            return result;
        },

           /**
         * Returns interactive activity nodes enriched with runtime WorkflowState data.
         * For ForEach child tasks, returns one entry per subprocess iteration with
         * interpolated owner/supervisor/contributor fields ({burst} replaced).
         */
        getAllActivitiesWithState(state): EnrichedProcessNode[] {
            var result: EnrichedProcessNode[] = [];
            if (!state.Workflow) return result;

            var wfState = state.WorkflowState as api.ProcessActivityExecutionState[] | null;
            var wfStateIndex = state.WorkflowStateByNodeId;
            var useWorkflowStatePerfIndex = state.WorkflowStatePerfIndexEnabled;

            // Comprehensive interpolation for ForEach fields.
            // Handles: {burst}, {{Subprocess}.Name}, {<guid>.Name}, and remaining {…} tokens
            var interpolateForEach = function (value: string | null | undefined, memberName: string): string | null {
                if (!value) return null;
                var result = value;
                // 1. Replace {burst} (most common pattern)
                result = result.replace(/\{burst\}/gi, memberName);
                // 2. Replace {{Subprocess}.Name} (nested subprocess name reference)
                result = result.replace(/\{\{Subprocess\}\.Name\}/gi, memberName);
                // 3. Replace {Subprocess.Name} (alternative format)
                result = result.replace(/\{Subprocess\.Name\}/gi, memberName);
                // 4. Replace {<guid>.Name} where guid is a UUID pattern
                result = result.replace(/\{[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.Name\}/gi, memberName);
                // 5. Replace bare {Subprocess} token if still present
                result = result.replace(/\{Subprocess\}/gi, memberName);
                return result;
            };

            // Phase 1: Walk the tree to map child task nodeIds → their ForEach parent nodeId
            var childToForeachParent: { [childNodeId: string]: string } = {};
            var collectMappings = function (process: api.InteractiveProcess, parentForeachNodeId: string | null) {
                if (!process.nodes) return;
                for (var node of process.nodes!) {
                    if (node.activity instanceof api.ForeachProcessActivity) {
                        if (node.activity.subProcess)
                            collectMappings(node.activity.subProcess, node.id!);
                    } else if (node.activity instanceof api.SubProcessProcessActivity) {
                        if (node.activity.subProcess)
                            collectMappings(node.activity.subProcess, parentForeachNodeId);
                    } else if (node.activity instanceof api.InteractiveProcessActivity) {
                        if (parentForeachNodeId) {
                            childToForeachParent[node.id!] = parentForeachNodeId;
                        }
                    }
                }
            };
            collectMappings(state.Workflow, null);

            // Phase 2: Build iterations per ForEach parent using child nodeIds from WorkflowState
            var foreachIterations: { [foreachNodeId: string]: { subProcessRunId: string; parentCaption: string }[] } = {};
            if (wfState) {
                for (var entry of wfState) {
                    var parentId = entry.nodeId ? childToForeachParent[entry.nodeId] : null;
                    if (parentId &&
                        entry.subProcessRunId && entry.subProcessRunId !== '00000000-0000-0000-0000-000000000000') {
                        if (!foreachIterations[parentId])
                            foreachIterations[parentId] = [];
                        var existing = foreachIterations[parentId].find(
                            it => it.subProcessRunId === entry.subProcessRunId
                        );
                        if (!existing) {
                            foreachIterations[parentId].push({
                                subProcessRunId: entry.subProcessRunId,
                                parentCaption: entry.parentCaption ?? ''
                            });
                        }
                    }
                }
            }

            // Walk the process tree, enriching nodes with state
            var getAllInner = function (process: api.InteractiveProcess, parentForeachNodeId: string | null) {
                if (!process.nodes) return;
                for (var node of process.nodes!) {
                    if (node.activity instanceof api.ForeachProcessActivity) {
                        if (node.activity.subProcess)
                            getAllInner(node.activity.subProcess, node.id!);
                    } else if (node.activity instanceof api.SubProcessProcessActivity) {
                        if (node.activity.subProcess)
                            getAllInner(node.activity.subProcess, parentForeachNodeId);
                    } else if (node.activity instanceof api.NoteProcessActivity ||
                               node.activity instanceof api.StartProcessActivity) {
                        continue;
                    } else if (node.activity instanceof api.InteractiveProcessActivity) {
                        if (parentForeachNodeId && foreachIterations[parentForeachNodeId]) {
                            // Inside a ForEach: create one enriched node per iteration
                            var iterations = foreachIterations[parentForeachNodeId];
                            var nodeEntries = useWorkflowStatePerfIndex ? (wfStateIndex.get(node.id!) ?? []) : null;
                            for (var iteration of iterations) {
                                var stateEntry = useWorkflowStatePerfIndex
                                    ? nodeEntries!.find(s => s.subProcessRunId === iteration.subProcessRunId)
                                    : wfState?.find(
                                        s => s.nodeId === node.id &&
                                             s.subProcessRunId === iteration.subProcessRunId
                                    );
                                var enriched = new api.InteractiveProcessNode() as EnrichedProcessNode;
                                enriched.id = node.id;
                                enriched.left = node.left;
                                enriched.top = node.top;
                                enriched.w = node.w;
                                enriched.h = node.h;
                                enriched.type = node.type;
                                enriched.text = node.text;
                                enriched.activity = node.activity;

                                var memberName = iteration.parentCaption;
                                if (stateEntry) {
                                    enriched.state = stateEntry.calculatedStatus != null
                                        ? api.ActivityExecutionResultEnum[stateEntry.calculatedStatus]
                                        : 'Upcoming';
                                    enriched.percent = stateEntry.percentComplete ?? 0;
                                    enriched._owners = interpolateForEach(stateEntry.owners, memberName);
                                    enriched._supervisors = interpolateForEach(stateEntry.supervisors, memberName);
                                    enriched._contributors = interpolateForEach(stateEntry.contributors, memberName);
                                    enriched._activityName = interpolateForEach(stateEntry.activityName, memberName) ?? node.activity!.name;
                                    enriched._calculatedDueDate = stateEntry.calculatedDueDate;
                                } else {
                                    enriched.state = 'Upcoming';
                                    enriched.percent = 0;
                                    var iAct = node.activity as api.InteractiveProcessActivity;
                                    enriched._owners = interpolateForEach(iAct.owner, memberName);
                                    enriched._supervisors = interpolateForEach(iAct.supervisor, memberName);
                                    enriched._contributors = interpolateForEach(iAct.contributor, memberName);
                                    enriched._activityName = interpolateForEach(node.activity!.name, memberName) ?? node.activity!.name;
                                    enriched._calculatedDueDate = null;
                                }
                                enriched._parentCaption = memberName;
                                result.push(enriched);
                            }
                        } else {
                            var stateEntry = useWorkflowStatePerfIndex
                                ? (wfStateIndex.get(node.id!) ?? []).find(
                                    s => s.subProcessRunId === '00000000-0000-0000-0000-000000000000' || !s.subProcessRunId
                                )
                                : wfState?.find(
                                    s => s.nodeId === node.id &&
                                         (s.subProcessRunId === '00000000-0000-0000-0000-000000000000' || !s.subProcessRunId)
                                );
                            var enriched = new api.InteractiveProcessNode() as EnrichedProcessNode;
                            enriched.id = node.id;
                            enriched.left = node.left;
                            enriched.top = node.top;
                            enriched.w = node.w;
                            enriched.h = node.h;
                            enriched.type = node.type;
                            enriched.text = node.text;
                            enriched.activity = node.activity;

                            if (stateEntry) {
                                enriched.state = stateEntry.calculatedStatus != null
                                    ? api.ActivityExecutionResultEnum[stateEntry.calculatedStatus]
                                    : node.state;
                                enriched.percent = stateEntry.percentComplete ?? node.percent;
                                enriched._owners = stateEntry.owners ?? (node.activity as api.InteractiveProcessActivity).owner;
                                enriched._supervisors = stateEntry.supervisors ?? (node.activity as api.InteractiveProcessActivity).supervisor;
                                enriched._contributors = stateEntry.contributors ?? (node.activity as api.InteractiveProcessActivity).contributor;
                                enriched._activityName = stateEntry.activityName ?? node.activity!.name;
                                enriched._calculatedDueDate = stateEntry.calculatedDueDate;
                            } else {
                                enriched.state = node.state;
                                enriched.percent = node.percent;
                                var iAct = node.activity as api.InteractiveProcessActivity;
                                enriched._owners = iAct.owner;
                                enriched._supervisors = iAct.supervisor;
                                enriched._contributors = iAct.contributor;
                                enriched._activityName = node.activity!.name;
                                enriched._calculatedDueDate = null;
                            }
                            enriched._parentCaption = null;
                            result.push(enriched);
                        }
                    } else {
                        result.push(node as EnrichedProcessNode);
                    }
                }
            };
            getAllInner(state.Workflow, null);
            return result;
        },

        getNodeById: (state) => (nodeId: string): api.InteractiveProcessNode | null => {
            var workflow = state.ParentWorkflow[0];
            if (workflow == null)
                workflow = state.Workflow!;

            var getNodeByIdInner = function (workflow: api.InteractiveProcess, nodeId: string): api.InteractiveProcessNode | null {
                if (workflow != null && workflow.nodes != null)
                    for (var i = 0; i < workflow.nodes.length; i++) {
                        var node = workflow.nodes[i];
                        if (node != null && node.id == nodeId)
                            return node;
                        if (node.activity != null && node.activity instanceof (api.SubProcessProcessActivity)) {
                            var proc2 = (node.activity as api.SubProcessProcessActivity).subProcess;
                            if (proc2 != null && proc2.nodes != null) {
                                var result = getNodeByIdInner(proc2, nodeId);
                                if (result != null)
                                    return result;
                            }
                        }
                    }
                return null;
            }

            return getNodeByIdInner(workflow, nodeId);
        },

        getDataSource: (state) => (name: string): api.DataSource | null => {
            if (state.AvailableDataSources != null)
                for (var i = 0; i < state.AvailableDataSources.length; i++) {
                    var ds = state.AvailableDataSources[i]
                    if (ds != null && ds.name == name)
                        return ds;
                }
            return null;
        },

        getDataSources: (state) => (provider: api.DataSourceProvider): string[] => {
            var result: string[] = [];
            if (state.AvailableDataSources != null)
                for (var i = 0; i < state.AvailableDataSources.length; i++) {
                    var ds = state.AvailableDataSources[i]
                    if (ds != null && ds.provider == provider && ds.name != null)
                        result.push(ds.name);
                }
            return result;
        },

        getMoreDataSources: (state) => (providers: api.DataSourceProvider[]): string[] => {
            var result: string[] = [];
            if (state.AvailableDataSources != null)
                for (var i = 0; i < state.AvailableDataSources.length; i++) {
                    var ds = state.AvailableDataSources[i]
                    if (ds != null && ds.name != null && providers.includes(ds.provider))
                        result.push(ds.name);
                }
            return result.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        },
        getDataPipeline: (state) => (providers: api.DataSourceProvider[]): api.PipelineDefinitionItem[] => {
            var result: api.PipelineDefinitionItem[] = [];
            if (state.AvailableDataPipelines != null)
                for (var i = 0; i < state.AvailableDataPipelines.length; i++) {
                    var ds = state.AvailableDataPipelines[i]
                    if (ds != null && ds.name != null)
                        result.push(ds);
                }
            return result;
        },
        getIsEditable: (state) => {
            if (state.IsGlobalWorkflow === true) return false;
            return state.IsEditable;
        }
    }
};

export default WorkflowStore;
